import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ClientServices() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Create modal
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ service_name: '', service_code: '', service_category: '', status: 'planned', service_type: 'one_time', start_date: '', expected_end_date: '', source: 'manual' });
  const [saving, setSaving] = useState(false);

  const fetchServices = () => {
    if (!companyId || !clientId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);

    fetch(`${API}/companies/${companyId}/clients/${clientId}/services?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setServices(Array.isArray(d.data) ? d.data : []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchServices(); }, [companyId, clientId, search, statusFilter, API]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/clients/${clientId}/services`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setShowModal(false);
      setForm({ service_name: '', service_code: '', service_category: '', status: 'planned', service_type: 'one_time', start_date: '', expected_end_date: '', source: 'manual' });
      fetchServices();
    } catch { alert('Failed to add service'); }
    finally { setSaving(false); }
  };

  const statusColor = { planned: '#8b5cf6', active: '#10b981', on_hold: '#f59e0b', completed: '#3b82f6', cancelled: '#ef4444', archived: '#94a3b8' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={() => navigate(`/company/clients/${clientId}`)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '4px' }}>←</button>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>🔧 Client Services</h2>
        <button onClick={() => setShowModal(true)} style={btnPrimary}>+ Add Service</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input placeholder="Search services..." value={search} onChange={e => setSearch(e.target.value)} style={inputStyle} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="">All Statuses</option>
          <option value="planned">Planned</option>
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {initialLoading ? <p>Loading...</p> : (
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Start Date</th>
                <th style={thStyle}>End Date</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Service Owner</th>
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No services found</td></tr>
              ) : services.map(s => (
                <tr key={s.id}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{s.service_name}</div>
                    {s.service_code && <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.service_code}</div>}
                  </td>
                  <td style={tdStyle}>{s.service_category || '—'}</td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, background: (statusColor[s.status] || '#6b7280') + '20', color: statusColor[s.status] || '#6b7280' }}>
                      {s.status}
                    </span>
                  </td>
                  <td style={tdStyle}>{(s.service_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{s.start_date || '—'}</td>
                  <td style={tdStyle}>{s.expected_end_date || s.actual_end_date || '—'}</td>
                  <td style={tdStyle}>{(s.source || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{s.service_owner?.name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>Add Client Service</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Service Name *</label>
                <input value={form.service_name} onChange={e => setForm({ ...form, service_name: e.target.value })} style={modalInputStyle} placeholder="Service name" />
              </div>
              <div>
                <label style={labelStyle}>Service Code</label>
                <input value={form.service_code} onChange={e => setForm({ ...form, service_code: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <input value={form.service_category} onChange={e => setForm({ ...form, service_category: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={modalInputStyle}>
                  <option value="planned">Planned</option>
                  <option value="active">Active</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Service Type</label>
                <select value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} style={modalInputStyle}>
                  <option value="one_time">One Time</option>
                  <option value="fixed_term">Fixed Term</option>
                  <option value="recurring">Recurring</option>
                  <option value="ongoing">Ongoing</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Start Date</label>
                <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Expected End Date</label>
                <input type="date" value={form.expected_end_date} onChange={e => setForm({ ...form, expected_end_date: e.target.value })} style={modalInputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={btnCancel}>Cancel</button>
              <button onClick={handleCreate} disabled={saving || !form.service_name} style={btnPrimary}>{saving ? 'Adding...' : 'Add Service'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', minWidth: '200px' };
const selectStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', background: '#fff' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnCancel = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '600px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const modalInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
